/**
 * Chart hover readout.
 *
 * Replaces the SVG <title> tooltips the old charts used, which had a ~700ms OS
 * delay, could not be styled or themed, showed one series at a time, and were
 * unreachable from the keyboard.
 *
 * Composition rules this follows:
 *  - The VALUE leads: it is the high-contrast element, the series name is
 *    secondary. That is the legend's hierarchy inverted, because here the
 *    reader already knows the series and wants the number.
 *  - Series are keyed by a short STROKE of their colour, not a filled box — at
 *    tooltip density a filled swatch is data-weight ink doing a label's job.
 *  - All text goes in as JSX text nodes, so React escapes it. Series and
 *    category names come from the API (airline names, aircraft types, channel
 *    frequencies) and are untrusted; this never builds HTML from them.
 */
function ChartTooltip({ tip, containerWidth }) {
  if (!tip) return null;

  const { x, y, title, rows } = tip;

  // Flip to the left of the cursor when close to the right edge, so the
  // readout never gets clipped by the card.
  const ESTIMATED_W = 168;
  const flip = containerWidth != null && x + ESTIMATED_W + 16 > containerWidth;

  const style = {
    left: `${x}px`,
    top: `${y}px`,
    transform: flip ? 'translate(-100%, -50%) translateX(-12px)' : 'translate(0, -50%) translateX(12px)',
  };

  return (
    <div className="chart-tooltip" style={style} role="tooltip" aria-hidden="true">
      {title && <div className="chart-tooltip-title">{title}</div>}
      {(rows || []).map((r, i) => (
        <div className="chart-tooltip-row" key={i}>
          {r.color && (
            <span className="chart-tooltip-key" style={{ background: r.color }} />
          )}
          <span className="chart-tooltip-value">{r.value}</span>
          {r.name && <span className="chart-tooltip-name">{r.name}</span>}
        </div>
      ))}
    </div>
  );
}

export default ChartTooltip;
